/**
 * Command palette search for the platform-admin layout (Cmd+K).
 *
 * The palette searches this portal's pages and system screens, which the
 * portal itself defines. It lists no tenant and no integration: until
 * 2026-10-23 it carried seven tenant names and six vendor names as literals
 * (four custody vendors, two LOS vendors), none read from platform-admin-api,
 * each pointing at an invented tenant id or at the Integrations page (GH
 * #4047). Tenants are found on the Tenants page, which reads them from
 * platform-admin-api.
 */

import { FileTextIcon, GaugeIcon } from 'lucide-react';
import type { PaletteCategory, PaletteResult } from '@aaimplatform/ui';

export const SEARCH_CATEGORIES: PaletteCategory[] = [
  { id: 'pages', label: 'Pages', icon: FileTextIcon },
  { id: 'system', label: 'System', icon: GaugeIcon },
];

export const SEARCHABLE_ENTRIES: { id: string; title: string; category: string; href: string }[] = [
  // Pages
  { id: 'dashboard', title: 'Platform Dashboard', category: 'pages', href: '/' },
  { id: 'tenants', title: 'Tenants', category: 'pages', href: '/tenants' },
  { id: 'data-browser', title: 'Data Browser', category: 'Developer', href: '/data-browser' },
  { id: 'tasks', title: 'FI Tasks', category: 'pages', href: '/tasks' },
  { id: 'notification-routing', title: 'Pledge Notifications', category: 'pages', href: '/notification-routing' },
  { id: 'integrations', title: 'Integrations', category: 'pages', href: '/integrations' },
  { id: 'aaim-services', title: 'Aaim Services', category: 'pages', href: '/aaim-services' },
  { id: 'attom-api', title: 'ATTOM Property Data', category: 'pages', href: '/integrations/attom' },
  // 'signatures' search entry removed 2026-04-29 (Sprint C11 Lane 5): page deleted.
  { id: 'ucc-dashboard', title: 'UCC Filing Dashboard', category: 'pages', href: '/ucc' },
  { id: 'ucc-filings', title: 'UCC Filings', category: 'pages', href: '/ucc/filings' },
  { id: 'ucc-submissions', title: 'UCC Submissions', category: 'pages', href: '/ucc/submissions' },
  { id: 'ucc-entities', title: 'UCC Entities (Parties & Debtors)', category: 'pages', href: '/ucc/entities' },
  { id: 'ucc-search', title: 'UCC Debtor Search', category: 'pages', href: '/ucc/search' },
  { id: 'ucc-jurisdictions', title: 'UCC Jurisdictions', category: 'pages', href: '/ucc/jurisdictions' },
  { id: 'ucc-fees', title: 'UCC Fee Management', category: 'pages', href: '/ucc/fees' },
  { id: 'ucc-analytics', title: 'UCC Filing Analytics', category: 'pages', href: '/ucc/analytics' },
  { id: 'ucc-compliance', title: 'UCC Audit Trail', category: 'pages', href: '/ucc/compliance' },
  { id: 'property-valuations', title: 'Property Valuations', category: 'pages', href: '/collateral/valuations' },
  { id: 'title-searches', title: 'Title Searches', category: 'pages', href: '/collateral/title-searches' },
  { id: 'lien-verifications', title: 'Lien Verifications', category: 'pages', href: '/collateral/lien-verifications' },
  { id: 'settings', title: 'General Settings', category: 'pages', href: '/platform-config/general' },
  { id: 'identity-providers', title: 'Identity Providers', category: 'pages', href: '/identity-providers' },
  { id: 'roles', title: 'Roles & Permissions', category: 'pages', href: '/roles' },
  { id: 'regulatory-readiness', title: 'Regulatory Readiness', category: 'pages', href: '/regulatory' },
  { id: 'economic-modeling', title: 'Economic Modeling (Strategy Tools)', category: 'pages', href: '/strategy-tools' },
  { id: 'syndication-dashboard', title: 'Liquidity Network', category: 'pages', href: '/syndication' },
  { id: 'syndication-offerings', title: 'Participation Offerings', category: 'pages', href: '/syndication/offerings' },
  { id: 'syndication-positions', title: 'Participation Positions', category: 'pages', href: '/syndication/positions' },
  { id: 'syndication-crt', title: 'CRT Token Management', category: 'pages', href: '/syndication/crt' },
  { id: 'syndication-program-settings', title: 'CRT Program Settings', category: 'pages', href: '/syndication/program-settings' },
  { id: 'syndication-subscriptions-queue', title: 'CRT Subscriptions Queue', category: 'pages', href: '/syndication/subscriptions-queue' },
  { id: 'asset-classes', title: 'Asset Type', category: 'pages', href: '/asset-classes' },
  // System
  { id: 'feature-flags', title: 'Feature Flags', category: 'system', href: '/feature-flags' },
  { id: 'audit-logs', title: 'Audit Logs', category: 'system', href: '/audit-logs' },
  { id: 'system-health', title: 'System Health', category: 'system', href: '/system-health' },
];

/** Entries whose title contains the query, case-insensitive, in the category when one is given. */
export function searchPaletteEntries(query: string, category?: string): PaletteResult[] {
  const lowerQuery = query.toLowerCase();
  return SEARCHABLE_ENTRIES
    .filter(entry => {
      const matchesQuery = entry.title.toLowerCase().includes(lowerQuery);
      const matchesCategory = !category || entry.category === category;
      return matchesQuery && matchesCategory;
    })
    .map(({ id, title, category: cat }) => ({ id, title, category: cat }));
}

/** The route a selected result opens, or undefined for an id the palette does not list. */
export function paletteEntryHref(id: string): string | undefined {
  return SEARCHABLE_ENTRIES.find(entry => entry.id === id)?.href;
}
